import { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { SearchOutlined } from "@mui/icons-material";
import { AdminFolderAccess } from "types/interfaces/adminUsers";
import CustomScrollbars from "../CustomScrollbars";
import {
  useGetAdminUserFoldersQuery,
  useGrantFolderAccessMutation,
  useRevokeFolderAccessMutation,
} from "features/adminUsers/adminUsers";

interface Props {
  userId: number;
  /** If false, folders are read-only (view only); only User & Folder Manager can edit. */
  canEdit?: boolean;
  onSaveSuccess?: (message: string, severity: "success" | "error") => void;
}

function FolderRow({
  folder,
  checked,
  onToggle,
  disabled,
}: {
  folder: AdminFolderAccess;
  checked: boolean;
  onToggle: (id: number) => void;
  disabled?: boolean;
}) {
  const checkbox = (
    <Checkbox
      checked={checked}
      size="small"
      disableRipple
      disabled={disabled}
      sx={{ p: 0, flexShrink: 0 }}
    />
  );

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={1}
      py={0.6}
      px={0.5}
      sx={{
        borderRadius: 1,
        cursor: disabled ? "default" : "pointer",
        "&:hover": disabled ? undefined : { bgcolor: "rgba(0,0,0,0.03)" },
      }}
      onClick={() => !disabled && onToggle(folder.id)}
    >
      {disabled ? (
        <Tooltip
          title="User &amp; Folder Manager or Super Admin required to change folder access."
          placement="left"
        >
          <span>{checkbox}</span>
        </Tooltip>
      ) : (
        checkbox
      )}
      <Box flex={1} minWidth={0}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {folder.folder}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {folder.folder_prefix}
        </Typography>
      </Box>
      <Box display="flex" gap={0.5} flexShrink={0}>
        {folder.is_sftp_folder ? (
          <Chip
            label="SFTP"
            size="small"
            sx={{
              height: 16,
              fontSize: 10,
              fontWeight: 700,
              bgcolor: "rgba(21,101,192,0.10)",
              color: "#1565c0",
            }}
          />
        ) : folder.is_integration ? (
          <Chip
            label="Integration"
            size="small"
            sx={{ height: 16, fontSize: 10, fontWeight: 700, bgcolor: "#fff3e0", color: "#e65100" }}
          />
        ) : (
          <Chip
            label="Migration folder"
            size="small"
            sx={{
              height: 16,
              fontSize: 10,
              fontWeight: 700,
              bgcolor: "#fff8e1",
              color: "#ff8f00",
            }}
          />
        )}
        {folder.sync_status !== "ready" && (
          <Chip
            label={folder.sync_status}
            size="small"
            sx={{
              height: 16,
              fontSize: 10,
              fontWeight: 700,
              bgcolor: folder.sync_status === "syncing" ? "rgba(21,101,192,0.10)" : "rgba(211,47,47,0.10)",
              color: folder.sync_status === "syncing" ? "#1565c0" : "error.dark",
            }}
          />
        )}
      </Box>
    </Box>
  );
}

function ConfirmDialog({
  open,
  folders,
  pendingState,
  originalState,
  onConfirm,
  onCancel,
  isSaving,
  error,
}: {
  open: boolean;
  folders: AdminFolderAccess[];
  pendingState: Record<number, boolean>;
  originalState: Record<number, boolean>;
  onConfirm: () => void;
  onCancel: () => void;
  isSaving: boolean;
  error: boolean;
}) {
  const toGrant = folders.filter(
    (f) => pendingState[f.id] === true && originalState[f.id] === false
  );
  const toRevoke = folders.filter(
    (f) => pendingState[f.id] === false && originalState[f.id] === true
  );

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>Confirm Access Changes</DialogTitle>
      <DialogContent dividers>
        {toGrant.length > 0 && (
          <Box mb={toRevoke.length > 0 ? 2 : 0}>
            <Typography variant="caption" fontWeight={700} color="success.main" textTransform="uppercase" display="block" mb={0.75}>
              Grant access
            </Typography>
            {toGrant.map((f) => (
              <Typography key={f.id} variant="body2" sx={{ py: 0.25 }}>
                {f.folder}
              </Typography>
            ))}
          </Box>
        )}
        {toRevoke.length > 0 && (
          <Box>
            <Typography variant="caption" fontWeight={700} color="error.main" textTransform="uppercase" display="block" mb={0.75}>
              Revoke access
            </Typography>
            {toRevoke.map((f) => (
              <Typography key={f.id} variant="body2" sx={{ py: 0.25 }}>
                {f.folder}
              </Typography>
            ))}
          </Box>
        )}
      </DialogContent>
      {error && (
        <Box px={3} pb={1}>
          <Typography variant="caption" color="error">
            Failed to save changes. Please try again.
          </Typography>
        </Box>
      )}
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={isSaving} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={isSaving}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          {isSaving ? "Saving..." : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function UserManagementFoldersTab({ userId, canEdit = true, onSaveSuccess }: Props) {
  const { data: folders, isLoading } = useGetAdminUserFoldersQuery(userId);
  const [grant] = useGrantFolderAccessMutation();
  const [revoke] = useRevokeFolderAccessMutation();

  // pendingState: only contains folders whose state differs from server
  const [pendingState, setPendingState] = useState<Record<number, boolean>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [search, setSearch] = useState("");

  if (isLoading) {
    return (
      <Box px={2.5} pt={2}>
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} variant="text" height={40} sx={{ mb: 0.5 }} />
        ))}
      </Box>
    );
  }

  if (!folders || folders.length === 0) {
    return (
      <Box px={2.5} pt={3} display="flex" justifyContent="center">
        <Typography variant="body2" color="text.secondary">
          No folders available.
        </Typography>
      </Box>
    );
  }

  const originalState: Record<number, boolean> = Object.fromEntries(
    folders.map((f) => [f.id, f.has_access])
  );

  const handleToggle = (id: number) => {
    const original = originalState[id];
    setPendingState((prev) => {
      const hasPending = id in prev;
      if (hasPending) {
        // toggling back to original — remove from pending
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return { ...prev, [id]: !original };
    });
  };

  const hasPendingChanges = Object.keys(pendingState).length > 0;

  const filteredFolders = search.trim()
    ? folders.filter(
        (f) =>
          f.folder.toLowerCase().includes(search.toLowerCase()) ||
          f.folder_prefix.toLowerCase().includes(search.toLowerCase())
      )
    : folders;

  const handleSave = () => setConfirmOpen(true);

  const handleConfirm = async () => {
    setIsSaving(true);
    setSaveError(false);
    try {
      const promises = folders
        .filter((f) => f.id in pendingState)
        .map((f) =>
          pendingState[f.id]
            ? grant({ userId, folderId: f.id }).unwrap()
            : revoke({ userId, folderId: f.id }).unwrap()
        );
      await Promise.all(promises);
      setPendingState({});
      setConfirmOpen(false);
      onSaveSuccess?.("Folder access updated.", "success");
    } catch {
      setSaveError(true);
      onSaveSuccess?.("Failed to save folder changes.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => { setConfirmOpen(false); setSaveError(false); };

  return (
    <>
      <Box px={2} pt={1.5} pb={1}>
        <TextField
          size="small"
          placeholder="Search folders..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined sx={{ fontSize: 16, color: "text.disabled" }} />
              </InputAdornment>
            ),
          }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13 } }}
        />
      </Box>

      <CustomScrollbars sx={{ flex: 1, minHeight: 0, px: 2, pb: 1 }}>
        {filteredFolders.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" mt={2}>
            No folders match your search.
          </Typography>
        ) : filteredFolders.map((folder) => (
          <FolderRow
            key={folder.id}
            folder={folder}
            checked={folder.id in pendingState ? pendingState[folder.id] : folder.has_access}
            onToggle={handleToggle}
            disabled={!canEdit}
          />
        ))}
      </CustomScrollbars>

      <Box px={2} pb={2} pt={1}>
        <Tooltip
          title={
            !canEdit
              ? "User & Folder Manager or Super Admin required to change folder access."
              : ""
          }
        >
          <span>
            <Button
              variant="contained"
              fullWidth
              disabled={!canEdit || !hasPendingChanges}
              onClick={handleSave}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5 }}
            >
              Save Changes
            </Button>
          </span>
        </Tooltip>
      </Box>

      {canEdit && (
        <ConfirmDialog
          open={confirmOpen}
          folders={folders}
          pendingState={pendingState}
          originalState={originalState}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          isSaving={isSaving}
          error={saveError}
        />
      )}
    </>
  );
}
